import { useEffect, useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { Layout } from "../components/layout";
import { ServicesSection } from "../components/sections";
import { SEO } from "../components/SEO";
import { CheckCircle } from "lucide-react";
import { Link } from "react-router-dom";
import { sanitizeHTML } from "../utils/sanitize";
import { useCMSPage } from "../hooks/useCMSPage";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DEFAULTS = {
  hero_label: "Our Services",
  hero_title: "Web Solutions That",
  hero_title_accent: "Deliver",
  hero_subtitle: "From simple websites to complex content management systems, we have the expertise to bring your vision to life.",
  starter_name: "Starter",
  starter_price: "1,500",
  starter_description: "Perfect for small businesses just getting started online.",
  starter_features: "5-page responsive website\nMobile-friendly design\nContact form integration\nBasic SEO setup\n1 month support",
  professional_name: "Professional",
  professional_price: "3,500",
  professional_description: "Ideal for growing businesses that need more functionality.",
  professional_features: "10-page responsive website\nContent Management System\nBlog functionality\nAdvanced SEO optimization\nPhoto gallery\n3 months support",
  enterprise_name: "Enterprise",
  enterprise_price: "Custom",
  enterprise_description: "Full-featured solutions for established organizations.",
  enterprise_features: "Unlimited pages\nCustom CMS features\nE-commerce integration\nAdvanced analytics\nPriority support\nOngoing maintenance",
};

export const ServicesPage = () => {
  const [navItems, setNavItems] = useState([]);
  const { fields: f, content } = useCMSPage("services", DEFAULTS);

  useEffect(() => {
    axios.get(`${API}/navigation`).then((res) => setNavItems(res.data)).catch(console.error);
  }, []);

  const packages = [
    {
      name: f.starter_name,
      price: f.starter_price,
      description: f.starter_description,
      features: f.starter_features.split("\n").filter(Boolean),
    },
    {
      name: f.professional_name,
      price: f.professional_price,
      description: f.professional_description,
      features: f.professional_features.split("\n").filter(Boolean),
      featured: true,
    },
    {
      name: f.enterprise_name,
      price: f.enterprise_price,
      description: f.enterprise_description,
      features: f.enterprise_features.split("\n").filter(Boolean),
    },
  ];

  return (
    <Layout navItems={navItems}>
      <SEO
        title="Services"
        description="Professional web development services including website design, CMS development, e-commerce, and ongoing maintenance for small businesses and non-profits."
        keywords="web development services, website design, CMS development, e-commerce, website maintenance, small business services"
      />
      {/* Hero */}
      <section data-testid="services-hero" className="section-padding bg-its-teal-dark">
        <div className="section-container">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="text-center max-w-3xl mx-auto"
          >
            <span className="label-text">{f.hero_label}</span>
            <h1 className="heading-1 mt-2 mb-6">
              {f.hero_title}
              <span className="text-its-gold"> {f.hero_title_accent}</span>
            </h1>
            <p className="body-text">
              {f.hero_subtitle}
            </p>
          </motion.div>
        </div>
      </section>

      {/* Services Grid */}
      <ServicesSection location="services" />

      {/* Pricing */}
      <section className="section-padding bg-its-teal-dark">
        <div className="section-container">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <span className="label-text">Pricing</span>
            <h2 className="heading-2 mt-2">Investment Options</h2>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 pt-4">
            {packages.map((pkg, index) => (
              <motion.div
                key={pkg.name}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className={`glass-card relative overflow-visible ${
                  pkg.featured ? "border-its-gold ring-2 ring-its-gold/20" : ""
                }`}
              >
                {pkg.featured && (
                  <div className="absolute -top-4 left-1/2 -translate-x-1/2 bg-its-gold text-its-teal text-xs font-bold px-4 py-1.5 rounded-full whitespace-nowrap z-10">
                    Most Popular
                  </div>
                )}
                <div className="text-center mb-6">
                  <h3 className="text-xl font-semibold text-white mb-2">{pkg.name}</h3>
                  <div className="flex items-baseline justify-center gap-1">
                    <span className="text-3xl font-bold text-its-gold">
                      {pkg.price === "Custom" ? "" : "$"}
                      {pkg.price}
                    </span>
                  </div>
                  <p className="text-its-offwhite/80 text-sm mt-2">{pkg.description}</p>
                </div>
                <ul className="space-y-3 mb-6">
                  {pkg.features.map((feature) => (
                    <li key={feature} className="flex items-start gap-2 text-sm text-its-offwhite/90">
                      <CheckCircle size={16} className="text-its-teal-light mt-0.5 flex-shrink-0" />
                      {feature}
                    </li>
                  ))}
                </ul>
                <Link
                  to="/process"
                  className={`block text-center py-3 rounded-md font-semibold transition-colors ${
                    pkg.featured
                      ? "bg-its-gold text-its-teal hover:bg-its-gold-soft"
                      : "border border-its-teal-light text-white hover:bg-its-teal-light/10"
                  }`}
                >
                  Get Started
                </Link>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Additional CMS Content */}
      {content && (
        <section className="section-padding">
          <div className="section-container">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="max-w-3xl mx-auto prose-content"
              dangerouslySetInnerHTML={{ __html: sanitizeHTML(content) }}
            />
          </div>
        </section>
      )}
    </Layout>
  );
};

export default ServicesPage;
